import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Shield } from '../components/Icons.jsx';

const schools = [
  { name: 'University of Oxford', city: 'Oxford', cutoff: 2810, weight: 'Heavy', alevel: 'A*AA', notes: 'BMAT historically; now uses UCAT from 2024. Hard cutoff in the high 2700s/2800s for shortlisting.' },
  { name: 'University of Cambridge', city: 'Cambridge', cutoff: 2800, weight: 'Heavy', alevel: 'A*A*A', notes: 'Differs by college. UCAT weighted heavily alongside academic record.' },
  { name: 'Imperial College London', city: 'London', cutoff: 2700, weight: 'Heavy', alevel: 'AAA', notes: 'Top 30% UCAT threshold annually. Section minimums (DM &amp; QR usually 600+).' },
  { name: 'University College London (UCL)', city: 'London', cutoff: 2650, weight: 'Medium', alevel: 'A*AA', notes: 'UCAT is one of several factors. Personal statement and references count.' },
  { name: "King's College London (GKT)", city: 'London', cutoff: 2620, weight: 'Medium', alevel: 'A*AA', notes: 'Uses UCAT total + SJT band as part of pre-interview scoring.' },
  { name: 'University of Manchester', city: 'Manchester', cutoff: 2550, weight: 'Heavy', alevel: 'AAA', notes: 'UCAT threshold set each cycle, then ranks by UCAT for interview offers.' },
  { name: 'University of Edinburgh', city: 'Edinburgh', cutoff: 2700, weight: 'Heavy', alevel: 'AAA', notes: 'No interview. Heavy reliance on UCAT, GCSE and personal statement.' },
  { name: 'University of Bristol', city: 'Bristol', cutoff: 2470, weight: 'Medium', alevel: 'AAA', notes: 'Threshold + holistic; SJT Band 4 typically rejected.' },
  { name: 'University of Birmingham', city: 'Birmingham', cutoff: 2530, weight: 'Heavy', alevel: 'AAA', notes: 'Ranks by UCAT for interview. Section minimums apply.' },
  { name: 'Newcastle University', city: 'Newcastle', cutoff: 2440, weight: 'Heavy', alevel: 'AAA', notes: 'Cutoff updated annually; SJT also weighted. One of the more transparent.' },
  { name: 'University of Sheffield', city: 'Sheffield', cutoff: 2500, weight: 'Heavy', alevel: 'AAA', notes: 'Ranks by UCAT for interview offers. SJT Band 4 = automatic rejection.' },
  { name: 'University of Leeds', city: 'Leeds', cutoff: 2490, weight: 'Medium', alevel: 'AAA', notes: 'Holistic — UCAT below threshold can still get interview with strong PS.' },
  { name: 'Cardiff University', city: 'Cardiff', cutoff: 2530, weight: 'Medium', alevel: 'AAA', notes: 'Prefers SJT Band 1 or 2. Specifically values Welsh applicants.' },
  { name: 'University of Nottingham', city: 'Nottingham', cutoff: 2450, weight: 'Medium', alevel: 'AAA', notes: 'UCAT + GCSE scoring; SJT used as tiebreaker.' },
  { name: 'University of Southampton', city: 'Southampton', cutoff: 2380, weight: 'Medium', alevel: 'AAA', notes: 'Slightly lower threshold than peers; strong widening-participation focus.' },
  { name: 'University of Leicester', city: 'Leicester', cutoff: 2420, weight: 'Medium', alevel: 'AAA', notes: 'Uses UCAT + GCSEs + contextual flags.' },
  { name: 'University of Liverpool', city: 'Liverpool', cutoff: 2520, weight: 'Medium', alevel: 'AAA', notes: 'SJT weighted heavily here — Band 3+4 disadvantaged.' },
  { name: 'University of Glasgow', city: 'Glasgow', cutoff: 2570, weight: 'Heavy', alevel: 'AAA', notes: 'Hard reject on SJT Band 4. UCAT determines interview cutoff.' },
  { name: 'University of Aberdeen', city: 'Aberdeen', cutoff: 2400, weight: 'Medium', alevel: 'AAA', notes: 'Lower threshold than other Scottish schools. Open to non-Scotland applicants.' },
  { name: 'University of Dundee', city: 'Dundee', cutoff: 2480, weight: 'Heavy', alevel: 'AAA', notes: 'UCAT-heavy. Some flex for contextual / SWAP applicants.' },
  { name: 'Queen Mary University (Barts)', city: 'London', cutoff: 2580, weight: 'Heavy', alevel: 'A*AA', notes: 'Threshold-driven; SJT Band 4 rejected.' },
  { name: "St George's, London", city: 'London', cutoff: 2400, weight: 'Medium', alevel: 'AAA', notes: 'Famously friendly to UCAT scores 2400–2600. SJT used as a filter.' },
  { name: 'University of Lancaster', city: 'Lancaster', cutoff: 2400, weight: 'Light', alevel: 'AAA', notes: 'UCAT weighted lightly. Heavier reliance on academic background and interview.' },
  { name: 'Hull York Medical School', city: 'Hull/York', cutoff: 2370, weight: 'Medium', alevel: 'AAA', notes: 'Threshold + ranking. SJT can compensate for borderline UCAT.' },
  { name: 'University of Plymouth (Peninsula)', city: 'Plymouth', cutoff: 2420, weight: 'Heavy', alevel: 'AAA', notes: 'UCAT and SJT both weighted heavily into interview decision.' },
  { name: 'University of Exeter', city: 'Exeter', cutoff: 2440, weight: 'Heavy', alevel: 'AAA', notes: 'Ranks applicants by UCAT for interview shortlist.' },
  { name: 'University of East Anglia (UEA)', city: 'Norwich', cutoff: 2380, weight: 'Medium', alevel: 'AAA', notes: 'Holistic — UCAT used as one of many factors.' },
  { name: 'Anglia Ruskin', city: 'Chelmsford', cutoff: 2350, weight: 'Light', alevel: 'AAA', notes: 'Newer school. UCAT relatively flexible.' },
  { name: 'University of Sunderland', city: 'Sunderland', cutoff: 2330, weight: 'Light', alevel: 'AAA', notes: 'One of the more accessible thresholds. Holistic review.' },
  { name: 'Brighton & Sussex Medical School', city: 'Brighton', cutoff: 2420, weight: 'Medium', alevel: 'AAA', notes: 'Threshold + holistic; reports say SJT Band 4 disadvantaged but not auto-reject.' }
];

const sortOptions = [
  { value: 'best-fit', label: 'Best fit for me' },
  { value: 'cutoff-low', label: 'Lowest cutoff first' },
  { value: 'cutoff-high', label: 'Highest cutoff first' },
  { value: 'alpha', label: 'A–Z' }
];

export default function WhereCanIApply() {
  const [score, setScore] = useState(2500);
  const [weightFilter, setWeightFilter] = useState('all');
  const [sort, setSort] = useState('best-fit');

  const annotated = useMemo(() => {
    return schools.map((s) => {
      const delta = score - s.cutoff;
      let verdict, ord;
      if (delta >= 120) { verdict = 'Safe'; ord = 0; }
      else if (delta >= 40) { verdict = 'Competitive'; ord = 1; }
      else if (delta >= -40) { verdict = 'Borderline'; ord = 2; }
      else if (delta >= -120) { verdict = 'Stretch'; ord = 3; }
      else { verdict = 'Unlikely'; ord = 4; }
      return { ...s, delta, verdict, ord };
    });
  }, [score]);

  const filtered = useMemo(() => {
    let list = annotated;
    if (weightFilter !== 'all') list = list.filter((s) => s.weight === weightFilter);
    if (sort === 'best-fit') list = [...list].sort((a, b) => a.ord - b.ord || b.delta - a.delta);
    if (sort === 'cutoff-low') list = [...list].sort((a, b) => a.cutoff - b.cutoff);
    if (sort === 'cutoff-high') list = [...list].sort((a, b) => b.cutoff - a.cutoff);
    if (sort === 'alpha') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [annotated, weightFilter, sort]);

  const counts = useMemo(() => {
    const init = { Safe: 0, Competitive: 0, Borderline: 0, Stretch: 0, Unlikely: 0 };
    for (const s of annotated) init[s.verdict]++;
    return init;
  }, [annotated]);

  return (
    <>
      <div className="page-head">
        <h1>Where can I apply?</h1>
        <p className="subtitle">Set your UCAT — see who's safe, competitive, stretch and out of range.</p>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>Your UCAT total (VR + DM + QR)</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
          <input
            type="range"
            min="1800" max="2700" step="10"
            value={score}
            onChange={(e) => setScore(Number(e.target.value))}
            style={{ flex: 1, accentColor: 'var(--brand-purple)' }}
          />
          <input
            type="number"
            min="1800" max="2700" step="10"
            value={score}
            onChange={(e) => setScore(Math.max(1800, Math.min(2700, Number(e.target.value) || 0)))}
            style={{
              width: 110, padding: '10px 12px', fontSize: 18, fontWeight: 700,
              border: '1px solid var(--border-strong)', borderRadius: 10,
              textAlign: 'right', color: 'var(--text)'
            }}
          />
        </div>

        <div className="r-grid-5" style={{ marginTop: 16 }}>
          <Counter label="Safe" value={counts.Safe} colour="var(--live-green)" />
          <Counter label="Competitive" value={counts.Competitive} colour="var(--brand-purple)" />
          <Counter label="Borderline" value={counts.Borderline} colour="#f59e0b" />
          <Counter label="Stretch" value={counts.Stretch} colour="#f97316" />
          <Counter label="Unlikely" value={counts.Unlikely} colour="var(--text-muted)" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Filter:</span>
        {['all', 'Heavy', 'Medium', 'Light'].map((w) => (
          <button
            key={w}
            onClick={() => setWeightFilter(w)}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              border: `1px solid ${w === weightFilter ? 'var(--brand-purple)' : 'var(--border)'}`,
              background: w === weightFilter ? 'var(--accent-soft)' : '#fff',
              color: w === weightFilter ? 'var(--brand-deep)' : 'var(--text-2)',
              fontWeight: w === weightFilter ? 600 : 500,
              fontSize: 13
            }}
          >{w === 'all' ? 'All weighting' : `${w} UCAT weight`}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sort:</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', fontSize: 13 }}>
            {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--card-soft)' }}>
              <th style={cellHead}>School</th>
              <th style={cellHead}>City</th>
              <th style={cellHead}>Last-cycle cutoff</th>
              <th style={cellHead}>UCAT weight</th>
              <th style={cellHead}>A-level</th>
              <th style={cellHead}>Your chance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.name} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={cell}>
                  <strong style={{ color: 'var(--text)' }}>{s.name}</strong>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.notes}</div>
                </td>
                <td style={cell}>{s.city}</td>
                <td style={cell}>{s.cutoff}</td>
                <td style={cell}>{s.weight}</td>
                <td style={cell}>{s.alevel}</td>
                <td style={cell}>
                  <VerdictPill verdict={s.verdict} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 16 }}>
        Cutoffs are estimates based on last cycle's reported data and may shift year to year. Always verify on each university's official admissions page before applying.
      </p>

      <section className="parents-cta" style={{ marginTop: 32 }}>
        <div>
          <h2>Borderline at your top choice?</h2>
          <p>The gap between Borderline and Competitive is usually 80–120 UCAT points. That's pacing and section strategy — not raw knowledge. Talk to a Medic Mind tutor for an honest read.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book a free call <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

function Counter({ label, value, colour }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: colour, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function VerdictPill({ verdict }) {
  const colours = {
    Safe: { bg: '#e7f7ef', fg: 'var(--live-green)', bd: 'rgba(16, 185, 129, 0.35)' },
    Competitive: { bg: 'var(--accent-soft)', fg: 'var(--brand-purple)', bd: 'var(--accent-soft-2)' },
    Borderline: { bg: '#fef3c7', fg: '#b45309', bd: '#fde68a' },
    Stretch: { bg: '#ffedd5', fg: '#c2410c', bd: '#fed7aa' },
    Unlikely: { bg: '#f1f5f9', fg: 'var(--text-muted)', bd: 'var(--border)' }
  };
  const c = colours[verdict];
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 999,
      background: c.bg,
      color: c.fg,
      border: `1px solid ${c.bd}`,
      fontSize: 12,
      fontWeight: 700
    }}>{verdict}</span>
  );
}

const cellHead = { padding: '14px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 };
const cell = { padding: '14px 14px', fontSize: 14, verticalAlign: 'top' };
